import React from 'react';

interface LabelWithIconProps {
  icon: React.ReactNode;
  text: string;
  className?: 'validation-message' | 'validation-warning-message' | 'pending-message';
  iconSize?: number;
  iconStyle?: React.CSSProperties;
}

// Style definitions for different use cases
const styles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem'
  },
  validation: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem'
  },
  validationWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500
  },
  pending: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    color: '#757575'
  }
};

/**
 * A reusable component that displays an icon next to text
 * 
 * @param icon - The icon component to display (usually from lucide-react)
 * @param text - The text to display next to the icon
 * @param className - Optional CSS class for the container
 * @param iconSize - Size of the icon (defaults to 16px)
 * @param iconStyle - Optional custom styles for the icon
 */
export const LabelWithIcon: React.FC<LabelWithIconProps> = ({ 
  icon, 
  text, 
  className,
  iconSize = 16,
  iconStyle = { verticalAlign: 'middle', marginRight: '0.25rem' }
}) => {
  // Select the appropriate style based on className
  let styleToUse = styles.base;
  if (className === 'validation-message') {
    styleToUse = styles.validation;
  } else if (className === 'validation-warning-message') {
    styleToUse = styles.validationWarning;
  } else if (className === 'pending-message') {
    styleToUse = styles.pending;
  }

  return (
    <span className={className} style={styleToUse}>
      {React.cloneElement(icon as React.ReactElement, { 
        size: iconSize, 
        style: iconStyle 
      })} {text}
    </span>
  );
};

export default LabelWithIcon; 